import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import RecordPage, { FormSection } from '../../components/shell/RecordPage';
import PasswordField from '../../components/shell/PasswordField';
import { StatusBadge } from '../../components/datagrid/DataGrid';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import {
  CloudArrowUp,
  Warning,
  Database,
  VideoCamera,
  Image as ImageIcon,
  FileText,
  Gear,
  TestTube,
  CloudCheck,
  CloudSlash,
  FloppyDisk,
  ArrowCounterClockwise,
  ChartBar
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function StorageConfig() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  // Global storage config (for all tenants)
  const [globalConfig, setGlobalConfig] = useState({
    enabled: false,
    provider: 'cloudflare', // cloudflare, aws_s3, custom
    // Cloudflare R2 settings
    account_id: '',
    access_key_id: '',
    secret_access_key: '',
    bucket_name: '',
    public_url: '', // Custom public URL or R2.dev URL
    // Cloudflare Stream settings
    stream_api_token: '', // API token for Cloudflare Stream (video uploads)
    // Media streaming settings
    media_streaming_enabled: false,
    video_transcoding: false,
    thumbnail_generation: false,
    max_file_size_mb: 100,
    allowed_video_formats: ['mp4', 'webm', 'mov'],
    allowed_image_formats: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
    allowed_document_formats: ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx']
  });

  useEffect(() => {
    fetchStorageConfig();
  }, []);

  const fetchStorageConfig = async () => {
    try {
      const res = await api.get('/admin/storage/config');
      if (res.data) {
        setGlobalConfig(prev => ({ ...prev, ...res.data }));
      }
      setDirty(false);
    } catch (error) {
      // Config might not exist yet, that's okay
      console.log('No existing storage config found');
    } finally {
      setLoading(false);
    }
  };

  // Field updater: patches config and marks the form dirty
  const updateConfig = (patch) => {
    setGlobalConfig(prev => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/storage/config', globalConfig);
      toast.success('Storage configuration saved successfully');
      setDirty(false);
    } catch (error) {
      toast.error('Failed to save storage configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleReload = async () => {
    setConnectionStatus(null);
    await fetchStorageConfig();
    toast.info('Configuration reloaded');
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setConnectionStatus(null);
    try {
      const res = await api.post('/admin/storage/test', {
        account_id: globalConfig.account_id,
        access_key_id: globalConfig.access_key_id,
        secret_access_key: globalConfig.secret_access_key,
        bucket_name: globalConfig.bucket_name,
        stream_api_token: globalConfig.stream_api_token || null
      });

      // Handle different response scenarios
      if (res.data.success) {
        setConnectionStatus({ success: true, message: res.data.message || 'Connection successful!' });
        toast.success('Connection test successful!');
      } else if (res.data.config_saved) {
        // Config saved but test failed (SSL issue)
        setConnectionStatus({
          success: 'partial',
          message: res.data.message
        });
        if (res.data.is_preview) {
          toast.info('Configuration saved! SSL test failed in preview - deploy to production to verify.');
        } else {
          toast.warning(res.data.message || 'Connection test failed, but configuration was saved.');
        }
      } else {
        setConnectionStatus({ success: false, message: res.data.message });
        toast.error(res.data.message || 'Connection test failed');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Connection test failed';
      setConnectionStatus({ success: false, message });
      toast.error(message);
    } finally {
      setTesting(false);
    }
  };

  const canTest = !!(globalConfig.account_id && globalConfig.access_key_id && globalConfig.secret_access_key && globalConfig.bucket_name);

  if (loading) {
    return (
      <DashboardLayout>
        <RecordPage title="Storage & media" loading onBack={null} data-testid="storage-config-page" />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="storage-config-page"
        title="Storage & media"
        subtitle="Cloudflare R2 object storage and Stream video"
        badge={<StatusBadge value={globalConfig.enabled ? 'enabled' : 'disabled'} />}
        onBack={null}
        onRefresh={fetchStorageConfig}
        refreshing={loading}
        dirty={dirty}
        primary={[
          { key: 'save', label: saving ? 'Saving…' : 'Save', icon: FloppyDisk, onClick: handleSave, disabled: saving, testid: 'save-config-btn' },
          { key: 'test', label: testing ? 'Testing…' : 'Test connection', icon: TestTube, variant: 'outline', onClick: handleTestConnection, disabled: testing || !canTest, testid: 'test-connection-btn' },
        ]}
        secondary={[
          { key: 'reload', label: 'Reload', icon: ArrowCounterClockwise, onClick: handleReload, disabled: saving || testing, title: 'Reload saved configuration (discards unsaved edits)' },
        ]}
      >
        {connectionStatus && (
          <div
            className={`flex items-start gap-2 text-sm p-3 rounded-lg border ${
              connectionStatus.success === true ? 'border-green-200 bg-green-50 text-green-700' :
              connectionStatus.success === 'partial' ? 'border-amber-200 bg-amber-50 text-amber-700' :
              'border-red-200 bg-red-50 text-red-700'
            }`}
            data-testid="connection-status"
          >
            {connectionStatus.success === true ? (
              <CloudCheck className="w-5 h-5 flex-shrink-0" weight="fill" />
            ) : connectionStatus.success === 'partial' ? (
              <Warning className="w-5 h-5 flex-shrink-0" weight="fill" />
            ) : (
              <CloudSlash className="w-5 h-5 flex-shrink-0" weight="fill" />
            )}
            <span className="whitespace-pre-wrap">{connectionStatus.message}</span>
          </div>
        )}

        <Tabs defaultValue="object-storage" className="space-y-4">
          <TabsList>
            <TabsTrigger value="object-storage" className="flex items-center gap-2">
              <CloudArrowUp className="w-4 h-4" />
              Object Storage
            </TabsTrigger>
            <TabsTrigger value="media" className="flex items-center gap-2">
              <VideoCamera className="w-4 h-4" />
              Media Settings
            </TabsTrigger>
          </TabsList>

          {/* Object Storage Tab */}
          <TabsContent value="object-storage" className="space-y-4">
            {/* Enable/Disable + R2 credentials */}
            <FormSection
              title="Cloudflare R2 Object Storage"
              icon={Database}
              description="Enable Cloudflare R2 for storing all files, videos, and media across the platform"
              actions={
                <Switch
                  checked={globalConfig.enabled}
                  onCheckedChange={(checked) => updateConfig({ enabled: checked })}
                  data-testid="storage-enabled-switch"
                />
              }
              bodyClassName={globalConfig.enabled ? 'p-4 space-y-6' : 'p-0'}
            >
              {globalConfig.enabled && (
                <>
                  {/* Provider Info */}
                  <div className="p-4 rounded-lg bg-blue-50 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
                    <div className="flex items-start gap-3">
                      <CloudArrowUp className="w-5 h-5 text-blue-600 mt-0.5" weight="fill" />
                      <div>
                        <p className="font-medium text-blue-800 dark:text-blue-200">Cloudflare R2</p>
                        <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                          S3-compatible object storage with zero egress fees. Perfect for video streaming and large file storage.
                        </p>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Cloudflare R2 Credentials */}
                  <div className="space-y-4">
                    <h3 className="font-medium flex items-center gap-2">
                      <Gear className="w-4 h-4" />
                      R2 Credentials
                    </h3>

                    <div className="grid gap-4">
                      <div className="field">
                        <Label htmlFor="account_id">Cloudflare Account ID</Label>
                        <Input
                          id="account_id"
                          placeholder="Enter your Cloudflare Account ID"
                          value={globalConfig.account_id}
                          onChange={(e) => updateConfig({ account_id: e.target.value })}
                          data-testid="account-id-input"
                        />
                        <p className="field-hint">
                          Found in your Cloudflare dashboard under R2 → Overview
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="field">
  <PasswordField id="access_key_id" label="Access Key ID" value={globalConfig.access_key_id} onChange={(v) => updateConfig({ access_key_id: v })} placeholder="R2 Access Key ID" minLength={1} autoComplete="off" testid="access-key-input" />
                        </div>
                        <div className="field">
  <PasswordField id="secret_access_key" label="Secret Access Key" value={globalConfig.secret_access_key} onChange={(v) => updateConfig({ secret_access_key: v })} placeholder="R2 Secret Access Key" minLength={1} autoComplete="off" testid="secret-key-input" />
                        </div>
                      </div>

                      <div className="field">
                        <Label htmlFor="bucket_name">Bucket Name</Label>
                        <Input
                          id="bucket_name"
                          placeholder="my-lms-storage"
                          value={globalConfig.bucket_name}
                          onChange={(e) => updateConfig({ bucket_name: e.target.value })}
                          data-testid="bucket-name-input"
                        />
                      </div>

                      <div className="field">
                        <Label htmlFor="public_url">Public URL (Optional)</Label>
                        <Input
                          id="public_url"
                          placeholder="https://pub-xxx.r2.dev or https://cdn.yourdomain.com"
                          value={globalConfig.public_url}
                          onChange={(e) => updateConfig({ public_url: e.target.value })}
                          data-testid="public-url-input"
                        />
                        <p className="field-hint">
                          Optional: Custom domain or R2.dev public URL for serving files
                        </p>
                      </div>

                      {/* Cloudflare Stream API Token */}
                      <div className="space-y-2 pt-4 border-t">
                        <PasswordField
                          id="stream_api_token"
                          label="Cloudflare Stream API Token"
                          placeholder="Your Cloudflare Stream API token"
                          value={globalConfig.stream_api_token}
                          onChange={(v) => updateConfig({ stream_api_token: v })}
                          minLength={1}
                          autoComplete="off"
                          testid="stream-token-input"
                        />
                        <p className="field-hint">
                          Required for video uploads. Get from Cloudflare Dashboard → Stream → API Tokens.
                          Videos will be uploaded to Stream, other files to R2.
                        </p>
                      </div>
                    </div>

                    {!canTest && (
                      <p className="field-hint">
                        Fill in the Account ID, Access Key, Secret Key and Bucket to enable “Test connection” in the toolbar.
                      </p>
                    )}
                  </div>
                </>
              )}
            </FormSection>

            {/* Current Status */}
            <FormSection title="Storage Status" icon={ChartBar}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 rounded-lg bg-muted">
                  <p className="text-sm text-muted-foreground">Provider</p>
                  <p className="font-medium mt-1">
                    {globalConfig.enabled ? 'Cloudflare R2' : 'Emergent Storage'}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-muted">
                  <p className="text-sm text-muted-foreground">Status</p>
                  <div className="flex items-center gap-2 mt-1">
                    {globalConfig.enabled && globalConfig.bucket_name ? (
                      <StatusBadge value="configured" tone="success" />
                    ) : globalConfig.enabled ? (
                      <StatusBadge value="pending setup" tone="warning" />
                    ) : (
                      <StatusBadge value="using default" tone="muted" />
                    )}
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-muted">
                  <p className="text-sm text-muted-foreground">Bucket</p>
                  <p className="font-medium mt-1 truncate">
                    {globalConfig.bucket_name || '-'}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-muted">
                  <p className="text-sm text-muted-foreground">Public URL</p>
                  <p className="font-medium mt-1 truncate text-sm">
                    {globalConfig.public_url || 'Auto-generated'}
                  </p>
                </div>
              </div>
            </FormSection>
          </TabsContent>

          {/* Media Settings Tab */}
          <TabsContent value="media" className="space-y-4">
            {/* Cloudflare Stream Configuration */}
            <FormSection
              title="Cloudflare Stream"
              icon={VideoCamera}
              description="Configure Cloudflare Stream for video hosting and adaptive streaming"
              actions={
                <Switch
                  checked={globalConfig.media_streaming_enabled}
                  onCheckedChange={(checked) => updateConfig({ media_streaming_enabled: checked })}
                  data-testid="media-streaming-switch"
                />
              }
              bodyClassName={globalConfig.media_streaming_enabled ? 'p-4 space-y-6' : 'p-0'}
            >
              {globalConfig.media_streaming_enabled && (
                <>
                  {/* Cloudflare Stream Info */}
                  <div className="p-4 rounded-lg bg-orange-50 border border-orange-200 dark:bg-orange-900/20 dark:border-orange-800">
                    <div className="flex items-start gap-3">
                      <VideoCamera className="w-5 h-5 text-orange-600 mt-0.5" weight="fill" />
                      <div>
                        <p className="font-medium text-orange-800 dark:text-orange-200">Cloudflare Stream</p>
                        <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                          Video streaming with adaptive bitrate, automatic transcoding, and global delivery.
                        </p>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Cloudflare Stream Credentials */}
                  <div className="space-y-4">
                    <h3 className="font-medium flex items-center gap-2">
                      <Gear className="w-4 h-4" />
                      Stream Credentials
                    </h3>

                    <div className="grid gap-4">
                      <div className="field">
                        <Label htmlFor="stream_account_id">Cloudflare Account ID</Label>
                        <Input
                          id="stream_account_id"
                          placeholder="Enter your Cloudflare Account ID"
                          value={globalConfig.stream_account_id || ''}
                          onChange={(e) => updateConfig({ stream_account_id: e.target.value })}
                          data-testid="stream-account-id-input"
                        />
                      </div>

                      <div className="field">
  <PasswordField id="media_stream_api_token" label="Stream API Token" value={globalConfig.stream_api_token || ''} onChange={(v) => updateConfig({ stream_api_token: v })} placeholder="Cloudflare Stream API Token" minLength={1} autoComplete="off" testid="stream-api-token-input" />
                        <p className="field-hint">
                          Create an API token with Stream:Edit permissions in Cloudflare dashboard
                        </p>
                      </div>

                      <div className="field">
                        <Label htmlFor="stream_signing_key">Signing Key ID (Optional)</Label>
                        <Input
                          id="stream_signing_key"
                          placeholder="For signed URLs (private videos)"
                          value={globalConfig.stream_signing_key_id || ''}
                          onChange={(e) => updateConfig({ stream_signing_key_id: e.target.value })}
                          data-testid="stream-signing-key-input"
                        />
                      </div>

                      <div className="field">
  <PasswordField id="stream_signing_secret" label="Signing Key Secret (Optional)" value={globalConfig.stream_signing_secret || ''} onChange={(v) => updateConfig({ stream_signing_secret: v })} placeholder="For signed URLs (private videos)" minLength={1} autoComplete="off" testid="stream-signing-secret-input" />
                      </div>

                      <div className="field">
                        <Label htmlFor="stream_subdomain">Custom Subdomain (Optional)</Label>
                        <Input
                          id="stream_subdomain"
                          placeholder="customer-xxxxx.cloudflarestream.com"
                          value={globalConfig.stream_subdomain || ''}
                          onChange={(e) => updateConfig({ stream_subdomain: e.target.value })}
                          data-testid="stream-subdomain-input"
                        />
                        <p className="field-hint">
                          Your Cloudflare Stream subdomain for video playback
                        </p>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Stream Settings */}
                  <div className="space-y-4">
                    <h3 className="font-medium">Stream Settings</h3>
                    <div className="grid gap-4">
                      <div className="flex items-center justify-between p-4 rounded-lg border">
                        <div>
                          <p className="font-medium" data-find="Automatic Transcoding">Automatic Transcoding</p>
                          <p className="text-sm text-muted-foreground">
                            Transcode videos for HLS adaptive bitrate streaming
                          </p>
                        </div>
                        <Switch
                          checked={globalConfig.video_transcoding}
                          onCheckedChange={(checked) => updateConfig({ video_transcoding: checked })}
                          data-testid="video-transcoding-switch"
                        />
                      </div>

                      <div className="flex items-center justify-between p-4 rounded-lg border">
                        <div>
                          <p className="font-medium" data-find="Thumbnail Generation">Thumbnail Generation</p>
                          <p className="text-sm text-muted-foreground">
                            Auto-generate video thumbnails at upload
                          </p>
                        </div>
                        <Switch
                          checked={globalConfig.thumbnail_generation}
                          onCheckedChange={(checked) => updateConfig({ thumbnail_generation: checked })}
                          data-testid="thumbnail-generation-switch"
                        />
                      </div>

                      <div className="flex items-center justify-between p-4 rounded-lg border">
                        <div>
                          <p className="font-medium" data-find="Require Signed URLs">Require Signed URLs</p>
                          <p className="text-sm text-muted-foreground">
                            Restrict video access with time-limited signed URLs
                          </p>
                        </div>
                        <Switch
                          checked={globalConfig.require_signed_urls || false}
                          onCheckedChange={(checked) => updateConfig({ require_signed_urls: checked })}
                          data-testid="signed-urls-switch"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </FormSection>

            {/* File Limits */}
            <FormSection title="File Upload Limits" icon={FileText} bodyClassName="p-4 space-y-6">
              <div className="field">
                <Label htmlFor="max_file_size_mb">Maximum File Size (MB)</Label>
                <Input
                  id="max_file_size_mb"
                  type="number"
                  min="1"
                  max="5000"
                  value={globalConfig.max_file_size_mb}
                  onChange={(e) => updateConfig({ max_file_size_mb: parseInt(e.target.value) || 100 })}
                  data-testid="max-file-size-input"
                />
              </div>

              <Separator />

              <div className="space-y-4">
                <div className="field">
                  <Label className="flex items-center gap-2">
                    <VideoCamera className="w-4 h-4" />
                    Allowed Video Formats
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {['mp4', 'webm', 'mov', 'avi', 'mkv'].map(format => (
                      <Badge
                        key={format}
                        variant={globalConfig.allowed_video_formats.includes(format) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => {
                          const formats = globalConfig.allowed_video_formats.includes(format)
                            ? globalConfig.allowed_video_formats.filter(f => f !== format)
                            : [...globalConfig.allowed_video_formats, format];
                          updateConfig({ allowed_video_formats: formats });
                        }}
                      >
                        .{format}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="field">
                  <Label className="flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" />
                    Allowed Image Formats
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].map(format => (
                      <Badge
                        key={format}
                        variant={globalConfig.allowed_image_formats.includes(format) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => {
                          const formats = globalConfig.allowed_image_formats.includes(format)
                            ? globalConfig.allowed_image_formats.filter(f => f !== format)
                            : [...globalConfig.allowed_image_formats, format];
                          updateConfig({ allowed_image_formats: formats });
                        }}
                      >
                        .{format}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="field">
                  <Label className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Allowed Document Formats
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'txt'].map(format => (
                      <Badge
                        key={format}
                        variant={globalConfig.allowed_document_formats.includes(format) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => {
                          const formats = globalConfig.allowed_document_formats.includes(format)
                            ? globalConfig.allowed_document_formats.filter(f => f !== format)
                            : [...globalConfig.allowed_document_formats, format];
                          updateConfig({ allowed_document_formats: formats });
                        }}
                      >
                        .{format}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </FormSection>
          </TabsContent>
        </Tabs>
      </RecordPage>
    </DashboardLayout>
  );
}
